// Das_techError.jsx
import "./OcipError.css";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Modal, Table } from "react-bootstrap";
import { getIvFieldValue } from "../../utils/ivErrorDataUtils";

const PROTECTION_CODES = [
  { number: 0, code: "0", alarm: "NORMAL", description: "고장 없음", type: "Normal" },
  { number: 1, code: "1", alarm: "EARTH FAULT", description: "누설 전류 검출", type: "Error" },
  { number: 2, code: "2", alarm: "OVER HEAT", description: "시스템 과열", type: "Error" },
  { number: 3, code: "3", alarm: "DC OVER VOLTAGE", description: "DC-LINK 과전압", type: "Error" },
  { number: 4, code: "4", alarm: "MC ERROR", description: "계통 연결 MC 오작동", type: "Error" },
  { number: 5, code: "5", alarm: "DC LINK ERROR", description: "DC-LINK 센싱 불량", type: "Error" },
  { number: 6, code: "6", alarm: "AC OVER CURRENT", description: "AC 출력 과전류", type: "Error" },
  { number: 7, code: "7", alarm: "PV ERROR", description: "PV 모듈 배선 역결선", type: "Error" },
  { number: 8, code: "8", alarm: "LINE CONNECTED", description: "STAND ALONE 시 계통 연결", type: "Error" },
  { number: 9, code: "9", alarm: "OVER CUR.2", description: "출력 IGBT 과전류", type: "Error" },
  { number: 10, code: "A", alarm: "OVER CUR.2(2)", description: "출력 IGBT(2) 과전류", type: "Error" },
  { number: 11, code: "B", alarm: "OVER CUR.2(3)", description: "출력 IGBT(3) 과전류", type: "Error" },
  { number: 12, code: "C", alarm: "OVER CUR.2(4)", description: "출력 IGBT(4) 과전류", type: "Error" },
  { number: 13, code: "D", alarm: "REVERSE PHASE", description: "계통 배선 역결선", type: "Error" },
  { number: 14, code: "E", alarm: "DC UNBALANCE", description: "DC-LINK 불균형", type: "Error" },
  { number: 15, code: "F", alarm: "DC UNDER VOLTAGE", description: "DC-LINK 저전압", type: "Error" },
  { number: 16, code: "G", alarm: "PV OVER VOLTAGE", description: "PV 과전압", type: "Error" },
  { number: 17, code: "H", alarm: "PV UNDER VOLTAGE", description: "PV 저전압", type: "Error" },
  { number: 18, code: "I", alarm: "DC OVER CURRENT", description: "DC 과전류", type: "Error" },
  { number: 19, code: "J", alarm: "INTERNAL COMMUNICATION FAULT", description: "내부 통신 이상", type: "Error" },
  { number: 20, code: "K", alarm: "LINE OVER FREQUENCY", description: "계통 과주파수", type: "Error" },
  { number: 21, code: "L", alarm: "LINE UNDER FREQUENCY", description: "계통 저주파수", type: "Error" },
  { number: 22, code: "M", alarm: "LINE OVER VOLTAGE", description: "계통 과전압", type: "Error" },
  { number: 23, code: "N", alarm: "LINE UNDER VOLTAGE", description: "계통 저전압", type: "Error" },
  { number: 24, code: "O", alarm: "DC OFFSET FAULT", description: "DC OFFSET 이상", type: "Error" },
  { number: 25, code: "P", alarm: "AC CURRENT UNBALANCE", description: "AC 전류 불균형", type: "Error" },
  { number: 26, code: "Q", alarm: "OVER CUR.2(5)", description: "출력 IGBT(5) 과전류", type: "Error" },
  { number: 27, code: "R", alarm: "OVER CUR.2(6)", description: "출력 IGBT(6) 과전류", type: "Error" },
  { number: 28, code: "S", alarm: "EMERGENCY STOP", description: "긴급 정지", type: "Error" },
  { number: 29, code: "T", alarm: "SPD FAULT", description: "SPD 이상", type: "Error" },
  { number: 30, code: "U", alarm: "SMPS FAULT", description: "SMPS 이상", type: "Error" },
  { number: 31, code: "V", alarm: "CURRENT LIMIT (DC & AC)", description: "전류 제한 경고", type: "Warning" },
  { number: 32, code: "W", alarm: "TEMPERATURE DERATING", description: "온도 제한 경고", type: "Warning" },
  { number: 33, code: "X", alarm: "FAN LOCKED", description: "FAN 동작 경고", type: "Warning" },
  { number: 34, code: "Y", alarm: "FAN LIFE", description: "FAN 수명 경고", type: "Warning" },
];

const Das_techError = ({ SelInverter }) => {
  const ivData = useSelector((state) => state.ivData || {});
  const [openModal, setOpenModal] = useState(null);

  const getField = (name) =>
    getIvFieldValue(ivData, name, SelInverter);

  const getNumberField = (name) => {
    const rawValue = getField(name);

    if (
      rawValue === null ||
      rawValue === undefined ||
      rawValue === ""
    ) {
      return null;
    }

    const value = Number(rawValue);
    return Number.isFinite(value) ? value : null;
  };

  const inverterState = getNumberField("state");
  const linePowerLoss = getNumberField("Pfault");
  const protectionNumber = getNumberField("fault0");

  const currentProtection = useMemo(
    () =>
      PROTECTION_CODES.find(
        (item) => item.number === protectionNumber
      ) || null,
    [protectionNumber]
  );

  const stateLabel =
    inverterState === null
      ? "N/A"
      : inverterState === 0
      ? "발전 중"
      : inverterState === 1
      ? "정지"
      : `알 수 없음 (${inverterState})`;

  const lineStateLabel =
    linePowerLoss === null
      ? "N/A"
      : linePowerLoss === 0
      ? "계통 정상"
      : linePowerLoss === 1
      ? "계통 불량"
      : `알 수 없음 (${linePowerLoss})`;

  const protectionLabel =
    protectionNumber === null
      ? "N/A"
      : currentProtection
      ? protectionNumber === 0
        ? "정상"
        : `${currentProtection.code} (${currentProtection.alarm})`
      : `알 수 없는 코드 (${protectionNumber})`;

  const isStateAbnormal =
    inverterState !== null && inverterState !== 0;

  const isLineAbnormal =
    linePowerLoss !== null && linePowerLoss !== 0;

  const isProtectionAbnormal =
    protectionNumber !== null && protectionNumber !== 0;

  const closeModal = () => setOpenModal(null);

  const renderModalContent = () => {
    if (openModal === "STATE") {
      return (
        <>
          <Modal.Header closeButton>
            <Modal.Title>인버터 동작 상태</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div style={{ marginBottom: 12 }}>
              <b>현재 상태:</b>{" "}
              {inverterState === null
                ? "N/A"
                : `${inverterState} (${stateLabel})`}
            </div>

            <Table striped bordered size="sm">
              <thead>
                <tr>
                  <th style={{ width: 100 }}>코드</th>
                  <th>상태</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  className={
                    inverterState === 0 ? "active-row" : ""
                  }
                >
                  <td>0</td>
                  <td>RUN / 발전 중</td>
                </tr>

                <tr
                  className={
                    inverterState === 1 ? "active-row" : ""
                  }
                >
                  <td>1</td>
                  <td>STOP / 정지</td>
                </tr>
              </tbody>
            </Table>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              닫기
            </Button>
          </Modal.Footer>
        </>
      );
    }

    if (openModal === "LINE") {
      return (
        <>
          <Modal.Header closeButton>
            <Modal.Title>계통 상태</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div style={{ marginBottom: 12 }}>
              <b>현재 상태:</b>{" "}
              {linePowerLoss === null
                ? "N/A"
                : `${linePowerLoss} (${lineStateLabel})`}
            </div>

            <Table striped bordered size="sm">
              <thead>
                <tr>
                  <th style={{ width: 100 }}>코드</th>
                  <th>상태</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  className={
                    linePowerLoss === 0 ? "active-row" : ""
                  }
                >
                  <td>0</td>
                  <td>계통 상태 양호</td>
                </tr>

                <tr
                  className={
                    linePowerLoss === 1 ? "active-row" : ""
                  }
                >
                  <td>1</td>
                  <td>계통 상태 불량</td>
                </tr>
              </tbody>
            </Table>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              닫기
            </Button>
          </Modal.Footer>
        </>
      );
    }

    if (openModal === "PROTECTION") {
      return (
        <>
          <Modal.Header closeButton>
            <Modal.Title>Protection 코드</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div style={{ marginBottom: 12 }}>
              <div>
                <b>현재 번호:</b>{" "}
                {protectionNumber ?? "N/A"}
              </div>

              <div>
                <b>수신 코드:</b>{" "}
                {currentProtection?.code ?? "N/A"}
              </div>

              <div>
                <b>알람:</b>{" "}
                {currentProtection?.alarm ?? "N/A"}
              </div>

              <div>
                <b>의미:</b>{" "}
                {currentProtection?.description ?? "N/A"}
              </div>
            </div>

            <Table striped bordered size="sm">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>번호</th>
                  <th style={{ width: 90 }}>수신 코드</th>
                  <th>알람</th>
                  <th>설명</th>
                  <th style={{ width: 90 }}>구분</th>
                </tr>
              </thead>

              <tbody>
                {PROTECTION_CODES.map((item) => (
                  <tr
                    key={item.number}
                    className={
                      protectionNumber === item.number
                        ? "active-row"
                        : ""
                    }
                  >
                    <td>{item.number}</td>
                    <td>{item.code}</td>
                    <td>{item.alarm}</td>
                    <td>{item.description}</td>
                    <td>{item.type}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              닫기
            </Button>
          </Modal.Footer>
        </>
      );
    }

    return null;
  };

  return (
    <div className="status-list-container">
      <div className="status-list">
        <div className="status-item">
          <span className="status-value">
            인버터 동작: {stateLabel}
          </span>

          <button
            type="button"
            onClick={() => setOpenModal("STATE")}
            title="인버터 동작 상태 보기"
          >
            <span
              className={
                isStateAbnormal ? "blinking-icon" : ""
              }
            >
              {isStateAbnormal ? "🔴" : "🟢"}
            </span>
          </button>
        </div>

        <div className="status-item">
          <span className="status-value">
            계통 상태: {lineStateLabel}
          </span>

          <button
            type="button"
            onClick={() => setOpenModal("LINE")}
            title="계통 상태 보기"
          >
            <span
              className={
                isLineAbnormal ? "blinking-icon" : ""
              }
            >
              {isLineAbnormal ? "🔴" : "🟢"}
            </span>
          </button>
        </div>

        <div className="status-item">
          <span className="status-value">
            Protection: {protectionLabel}
          </span>

          <button
            type="button"
            onClick={() => setOpenModal("PROTECTION")}
            title="Protection 코드표 보기"
          >
            <span
              className={
                isProtectionAbnormal ? "blinking-icon" : ""
              }
            >
              {isProtectionAbnormal ? "🔴" : "🟢"}
            </span>
          </button>
        </div>
      </div>

      {openModal !== null && (
        <Modal show onHide={closeModal} size="lg">
          {renderModalContent()}
        </Modal>
      )}
    </div>
  );
};

export default Das_techError;